import React, { useRef, useState, useCallback, useEffect } from "react";
import { useResourcesPlanner } from "../ResourcesPlannerContext";
import { getDatePosition, getDateSpanWidth, positionToDate, DAY_WIDTHS } from "../utils/dateUtils";
import AllocationActionMenu from "./AllocationActionMenu";
import styles from "../ResourcesPlanner.module.scss";
import moment from "moment";
import { getTimelineStatusColor } from "../../dashboard/utils/helper";
import { TimelineTaskIcons } from "../../../helpers/utils";

const DRAG_HANDLE_WIDTH = 6;
const DRAG_THRESHOLD = 3;

function countWorkingDaysInRange(startDate, endDate, includeWeekends = false) {
  const start = moment(startDate);
  const end = moment(endDate);
  if (end.isBefore(start, "day")) return 0;
  let count = 0;
  const cur = start.clone();
  while (cur.isSameOrBefore(end, "day")) {
    const dow = cur.day();
    if (includeWeekends || (dow !== 0 && dow !== 6)) count++;
    cur.add(1, "day");
  }
  return count;
}

function countAllDaysInRange(startDate, endDate) {
  const start = moment(startDate);
  const end = moment(endDate);
  if (end.isBefore(start, "day")) return 0;
  return end.diff(start, "days") + 1;
}

function snapToDay(pixelX, dayWidth) {
  return Math.round(pixelX / dayWidth) * dayWidth;
}

export default function AllocationBar({
  task,
  scope,
  project,
  resourceId,
  timelineStartDate,
  style: extraStyle = {},
}) {
  const { viewType, updateAllocation, openEditTask, openFloatingCard } = useResourcesPlanner();
  const dayWidth = DAY_WIDTHS[viewType];
  const alloc = task.allocation;

  const barRef = useRef(null);
  const pillRef = useRef(null);
  const menuRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);

  // Context menu state
  const [activeMenu, setActiveMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Live preview state for pill during drag
  const [previewPill, setPreviewPill] = useState(null);

  const left = alloc?.startDate ? getDatePosition(alloc.startDate, timelineStartDate, dayWidth) : null;
  const width = alloc?.startDate && alloc?.endDate ? getDateSpanWidth(alloc.startDate, alloc.endDate, dayWidth) : 0;

  // Hover state
  const [isHovered, setIsHovered] = useState(false);

  // Close menu on outside click or scroll
  useEffect(() => {
    if (!activeMenu) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setActiveMenu(false);
    };
    const handleScroll = () => setActiveMenu(false);
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [activeMenu]);

  // ── Context menu ────────────────────────────────────────────────────────
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const vh = window.innerHeight;
    const menuH = 80;
    const top = e.clientY + menuH > vh ? e.clientY - menuH : e.clientY;
    setMenuPosition({ top, left: e.clientX });
    setActiveMenu(true);
  }, []);

  const handleQuickView = useCallback(() => {
    setActiveMenu(false);
    openFloatingCard(task, scope, project, alloc, { x: menuPosition.left, y: menuPosition.top });
  }, [task, scope, project, alloc, menuPosition, openFloatingCard]);

  const handleEditAllocation = useCallback(() => {
    setActiveMenu(false);
    openEditTask(task.id);
  }, [task.id, openEditTask]);

  // ── Compute preview during drag ────────────────────────────────────────
  const computePreview = useCallback((newStartDate, newEndDate) => {
    const iw = alloc?.isIncludeWeekends ?? false;
    const hpd = alloc?.hoursPerDay ?? 0;
    const days = iw
      ? countAllDaysInRange(newStartDate, newEndDate)
      : countWorkingDaysInRange(newStartDate, newEndDate, false);
    const totalHours = parseFloat((days * hpd).toFixed(2));
    return { totalHours, hpd, days };
  }, [alloc]);

  // ── Unified mouse down for move/resize ──────────────────────────────────
  const handleMouseDown = useCallback(
    (e, type) => {
      if (e.button === 2) return; // Ignore right-click
      if (!alloc?.startDate || !alloc?.endDate || left === null || width <= 0) return;
      e.stopPropagation();
      e.preventDefault();

      dragStartRef.current = {
        type,
        x: e.clientX,
        y: e.clientY,
        left,
        width,
        startDate: alloc.startDate,
        endDate: alloc.endDate,
        hasMoved: false,
        _newStartDate: alloc.startDate,
        _newEndDate: alloc.endDate,
      };

      const handleMouseMove = (moveEvent) => {
        const ds = dragStartRef.current;
        if (!ds) return;

        const dx = moveEvent.clientX - ds.x;
        const dy = moveEvent.clientY - ds.y;

        // Check threshold
        if (!ds.hasMoved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        if (!ds.hasMoved) {
          ds.hasMoved = true;
          setIsDragging(true);
        }

        if (type === "move") {
          const snappedLeft = snapToDay(ds.left + dx, dayWidth);
          const newStartDate = positionToDate(snappedLeft, timelineStartDate, dayWidth);
          const daysDiff = moment(newStartDate).diff(moment(ds.startDate), "days");
          const newEndDate = moment(ds.endDate).add(daysDiff, "days").format("YYYY-MM-DD");

          if (barRef.current) {
            barRef.current.style.left = `${snappedLeft}px`;
          }
          ds._newStartDate = newStartDate;
          ds._newEndDate = newEndDate;

          const preview = computePreview(newStartDate, newEndDate);
          setPreviewPill(preview);

        } else if (type === "resize-left") {
          const rawLeft = ds.left + dx;
          const snappedLeft = snapToDay(rawLeft, dayWidth);
          // Enforce: left edge cannot go past (original right edge - dayWidth)
          const maxLeft = ds.left + ds.width - dayWidth;
          const clampedLeft = Math.min(snappedLeft, maxLeft);
          const newWidth = ds.left + ds.width - clampedLeft;

          if (newWidth >= dayWidth) {
            if (barRef.current) {
              barRef.current.style.left = `${clampedLeft}px`;
              barRef.current.style.width = `${newWidth}px`;
            }
            const newStartDate = positionToDate(clampedLeft, timelineStartDate, dayWidth);
            // Ensure start doesn't go past end
            if (moment(newStartDate).isAfter(moment(ds.endDate), "day")) return;
            ds._newStartDate = newStartDate;
            ds._newEndDate = ds.endDate;

            const preview = computePreview(newStartDate, ds.endDate);
            setPreviewPill(preview);
          }

        } else if (type === "resize-right") {
          const rawWidth = ds.width + dx;
          const snappedWidth = snapToDay(rawWidth, dayWidth);
          // Enforce minimum 1 day
          const finalWidth = Math.max(snappedWidth, dayWidth);

          if (barRef.current) {
            barRef.current.style.width = `${finalWidth}px`;
          }
          const newEndDate = positionToDate(ds.left + finalWidth - 1, timelineStartDate, dayWidth);
          // Ensure end doesn't go before start
          if (moment(newEndDate).isBefore(moment(ds.startDate), "day")) return;
          ds._newStartDate = ds.startDate;
          ds._newEndDate = newEndDate;

          const preview = computePreview(ds.startDate, newEndDate);
          setPreviewPill(preview);
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);

        const ds = dragStartRef.current;
        setIsDragging(false);
        setPreviewPill(null);

        if (!ds) return;

        if (!ds.hasMoved) {
          // No drag happened — treat as click → open edit
          dragStartRef.current = null;
          openEditTask(task.id);
          return;
        }

        const newStart = ds._newStartDate;
        const newEnd = ds._newEndDate;
        dragStartRef.current = null;

        if (newStart && newEnd && (newStart !== alloc.startDate || newEnd !== alloc.endDate)) {
          updateAllocation(task.id, resourceId, newStart, newEnd);
        } else if (barRef.current) {
          // Reset visual
          barRef.current.style.left = `${left}px`;
          barRef.current.style.width = `${width}px`;
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [left, width, alloc, dayWidth, timelineStartDate, task.id, resourceId, updateAllocation, openEditTask, computePreview]
  );

  const handleHover = (state) => {
    setIsHovered(state);
  };

  if (!alloc?.startDate || !alloc?.endDate || left === null || width <= 0) return null;

  // Pill display values
  const displayHpd = alloc.hoursPerDay ?? 0;
  const displayTotal = previewPill ? previewPill.totalHours : (alloc.totalHours ?? 0);

  return (
    <>
      <div
        ref={pillRef}
        className={styles.resourceAllocationPill}
        style={{
          position: 'absolute',
          left: `${left - 80}px`,
          top: `calc(${extraStyle.top} - 2px)`,
        }}
      >
        {
          (isDragging || isHovered) && (
            <span className={`${styles.allocationPill} ${styles.allocationPreviewPill} ${previewPill ? styles.pillPreviewNormal : styles.pillNormal}`}
              style={previewPill ? { background: '#e8f4fd !important', color: '#1976d2 !important' } : undefined}
            >
              {displayHpd}h • {displayTotal}hrs
            </span>
          )
        }

      </div>
      <div
        ref={barRef}
        className={`${styles.allocationBar} ${isDragging ? styles.allocationBarDragging : ""} ${getTimelineStatusColor(task.status, 0, false)}`}
        style={{ left: `${left + 2}px`, width: `${width - 2}px`, ...extraStyle }}
        onContextMenu={handleContextMenu}
      >
        <div
          className={`resize-handle-timeline absolute left-0 top-0 w-3 h-full cursor-ew-resize z-30`}
          onMouseDown={(e) => handleMouseDown(e, "resize-left")}
        />
        <div
          className={styles.barContent}
          onMouseDown={(e) => handleMouseDown(e, "move")}
          onMouseEnter={() => handleHover(true)}
          onMouseLeave={() => handleHover(false)}
        >
          {width > 30 && (
            <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center mr-1">
              {TimelineTaskIcons[task.status]()}
            </div>
          )}
          <span className={styles.barLabel}>{task.name}</span>
        </div>
        <div
          className={`resize-handle-timeline absolute right-0 top-0 w-3 h-full cursor-ew-resize z-30`}
          onMouseDown={(e) => handleMouseDown(e, "resize-right")}
        />
      </div>

      {activeMenu && (
        <AllocationActionMenu
          position={menuPosition}
          onQuickView={handleQuickView}
          onEditAllocation={handleEditAllocation}
          onClose={() => setActiveMenu(false)}
          menuRef={menuRef}
        />
      )}
    </>
  );
}
